import React from "react";
import {
  CheckCircleIcon,
  GlobeAltIcon,
  HeartIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
  ArrowUpRightIcon,
} from "@heroicons/react/24/outline";

import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";
import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";

import { Helmet } from "react-helmet-async";

export default function AboutUs() {
  const features = [
    {
      icon: UsersIcon,
      number: "01",
      title: "Trusted by Travelers",
      desc: "Travelers rely on Dubai Hotel Rates to discover comfortable stays and competitive hotel rates across Dubai.",
    },
    {
      icon: MapPinIcon,
      number: "02",
      title: "Great Locations",
      desc: "Explore hotels near Dubai's beaches, attractions, shopping destinations and major business districts.",
    },
    {
      icon: HeartIcon,
      number: "03",
      title: "Traveler First",
      desc: "We make finding the right hotel easier by keeping the search experience simple, clear and convenient.",
    },
    {
      icon: ShieldCheckIcon,
      number: "04",
      title: "Reliable & Convenient",
      desc: "Compare hotel options with confidence through a platform built around convenience and transparency.",
    },
  ];

  const stats = [
    { value: "500+", label: "Hotels" },
    { value: "10K+", label: "Travelers" },
    { value: "24/7", label: "Support" },
    { value: "25+", label: "Years Experience" },
  ];

  return (
    <>
      <Helmet>
        <title>About Us | Dubai Hotel Rates</title>

        <meta
          name="description"
          content="Learn more about Dubai Hotel Rates, your trusted platform for discovering hotels, comparing rates and finding great stays across Dubai."
        />

        <link
          rel="canonical"
          href="https://beachhoteldubai.com/about-us"
        />

        <meta
          property="og:title"
          content="About Us | Dubai Hotel Rates"
        />

        <meta
          property="og:description"
          content="Discover Dubai Hotel Rates and learn how we help travelers find better hotels, better rates and memorable stays in Dubai."
        />

        <meta
          property="og:url"
          content="https://beachhoteldubai.com/about-us"
        />

        <meta property="og:type" content="website" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About Us | Dubai Hotel Rates"
        />

        <meta
          name="twitter:description"
          content="Discover Dubai Hotel Rates and find comfortable stays and competitive hotel rates across Dubai."
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="w-full overflow-hidden bg-[#F5F8FA]">

        {/* HERO */}
        <Hero
          variant="inner"
          title="About Dubai Hotel Rates"
          subtitle="Helping travelers discover better hotels, better rates, and memorable stays across Dubai."
          backgroundImage={heroImg}
        />

        {/* STORY SECTION */}
        <section className="relative overflow-hidden bg-[#F5F8FA] py-16 sm:py-20 lg:py-28">

          <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-[#0B5552]/5 blur-3xl" />

          <div className="pointer-events-none absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-[#D7A74A]/10 blur-3xl" />

          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">

              {/* IMAGE */}
              <div className="relative mx-auto w-full max-w-xl lg:mx-0">

                <div className="relative overflow-hidden rounded-[28px] bg-[#DCE8ED] shadow-[0_30px_80px_rgba(15,35,50,0.12)]">

                  <img
                    src={aboutImg}
                    alt="Dubai Hotel Rates"
                    className="h-[360px] w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-[480px] lg:h-[570px]"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#102B35]/70 via-transparent to-transparent" />

                  {/* Image Caption */}
                  <div className="absolute bottom-7 left-7 right-7 sm:bottom-9 sm:left-9">

                    <div className="mb-3 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />
                      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                        Discover Dubai
                      </span>
                    </div>

                    <h3 className="max-w-sm text-2xl font-bold leading-tight text-white sm:text-3xl">
                      Your Journey Begins With the Right Stay
                    </h3>

                  </div>
                </div>

                {/* EXPERIENCE CARD */}
                <div className="absolute -bottom-8 left-4 rounded-2xl border border-white/80 bg-white px-6 py-5 shadow-[0_20px_60px_rgba(15,35,50,0.16)] sm:left-8 sm:px-8 sm:py-6">

                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#71808A]">
                    Our Experience
                  </p>

                  <div className="mt-1 flex items-end gap-2">
                    <span className="text-4xl font-extrabold leading-none text-[#8B0000] sm:text-5xl">
                      25+
                    </span>

                    <span className="mb-1 text-sm font-medium text-[#71808A]">
                      Years
                    </span>
                  </div>
                </div>

                {/* FLOATING BADGE */}
                <div className="absolute right-3 top-5 flex items-center gap-2 rounded-full border border-white/30 bg-[#0B5552]/90 px-4 py-2.5 shadow-lg backdrop-blur-md sm:right-5 sm:top-7">

                  <SparklesIcon className="h-4 w-4 text-[#E8C879]" />

                  <span className="text-xs font-semibold text-white">
                    Dubai Hotel Experts
                  </span>
                </div>

                {/* CORNER DETAIL */}
                <div className="absolute -left-3 -top-3 -z-10 h-24 w-24 rounded-tl-[30px] border-l-[5px] border-t-[5px] border-[#D7A74A] sm:-left-5 sm:-top-5" />

              </div>

              {/* CONTENT */}
              <div className="pt-5 lg:pt-0">

                <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-[#0B5552]/10 bg-white px-4 py-2 shadow-sm">

                  <span className="h-2 w-2 rounded-full bg-[#D7A74A]" />

                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B0000]">
                    Who We Are
                  </span>

                </div>

                <h2 className="text-3xl font-extrabold leading-[1.15] tracking-tight text-[#172B35] sm:text-4xl lg:text-[52px]">

                  Making Every Stay

                  <span className="mt-1 block text-[#8B0000]">
                    Worth Remembering
                  </span>

                </h2>

                <div className="mt-6 h-1 w-16 rounded-full bg-[#D7A74A]" />

                <p className="mt-7 text-sm leading-8 text-[#647681] sm:text-base">
                  Dubai Hotel Rates was created with a simple goal:
                  helping travelers find the right hotel without making
                  the search complicated. Whether you are visiting Dubai
                  for business, relaxation, shopping, or a memorable
                  getaway, we help you discover accommodation that fits
                  your needs.
                </p>

                <p className="mt-4 text-sm leading-8 text-[#647681] sm:text-base">
                  We bring together hotels, destinations and useful
                  travel information to make discovering Dubai stays
                  easier. Our focus is on convenience, value and helping
                  travelers make confident accommodation decisions.
                </p>

                {/* HIGHLIGHTS */}
                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">

                  <div className="flex items-start gap-3 rounded-2xl border border-[#DCE6EC] bg-white p-4 transition duration-300 hover:border-[#0B5552]/30 hover:shadow-md">

                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8F3F2]">
                      <CheckCircleIcon className="h-5 w-5 text-[#8B0000]" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-[#172B35]">
                        Carefully Selected Stays
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-[#71808A]">
                        Explore accommodation selected around comfort,
                        location, quality and value.
                      </p>
                    </div>

                  </div>

                  <div className="flex items-start gap-3 rounded-2xl border border-[#E9E0CE] bg-white p-4 transition duration-300 hover:border-[#D7A74A]/60 hover:shadow-md">

                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FBF5E8]">
                      <CheckCircleIcon className="h-5 w-5 text-[#B58A3C]" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-[#172B35]">
                        Value Focused
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-[#71808A]">
                        Discover competitive hotel rates while keeping
                        comfort and convenience in mind.
                      </p>
                    </div>

                  </div>

                </div>

                {/* STATS */}
                <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">

                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className="group rounded-2xl border border-[#DCE6EC] bg-white px-4 py-5 transition duration-300 hover:-translate-y-1 hover:border-[#0B5552]/30 hover:shadow-lg"
                    >

                      <div className="text-2xl font-extrabold tracking-tight text-[#8B0000] sm:text-3xl">
                        {stat.value}
                      </div>

                      <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[#71808A] sm:text-xs">
                        {stat.label}
                      </div>

                      <div className="mt-3 h-0.5 w-7 rounded-full bg-[#D7A74A] transition-all duration-300 group-hover:w-12" />

                    </div>
                  ))}

                </div>

              </div>
            </div>
          </div>
        </section>

        {/* MISSION & VISION */}
        <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28">

          <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#D7A74A]/10 blur-3xl" />

          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* HEADING */}
            <div className="mx-auto mb-14 max-w-2xl text-center">

              <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-[#0B5552]/10 bg-[#F1F7F7] px-4 py-2">

                <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />

                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B0000]">
                  Our Direction
                </span>

              </div>

              <h2 className="text-3xl font-extrabold tracking-tight text-[#172B35] sm:text-4xl lg:text-5xl">
                Built Around Better Travel
              </h2>

              <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-[#71808A] sm:text-base">
                Everything we do starts with one goal:
                creating a simpler and more enjoyable way
                to discover hotels and travel experiences in Dubai.
              </p>

            </div>

            {/* CARDS */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

              {/* MISSION */}
              <div className="group relative min-h-[370px] overflow-hidden rounded-[28px] bg-[#0B5552] p-7 shadow-[0_25px_70px_rgba(11,85,82,0.18)] transition duration-500 hover:-translate-y-1 sm:p-10 lg:p-12">

                <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full border border-white/10 transition-transform duration-700 group-hover:scale-110" />

                <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-[#D7A74A]/10 blur-3xl" />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#D7A74A] text-[#123C3B] shadow-lg">
                      <SparklesIcon className="h-8 w-8" />
                    </div>

                    <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8C879]">
                      Our Mission
                    </p>

                    <h3 className="mt-3 max-w-lg text-2xl font-extrabold leading-tight text-white sm:text-3xl lg:text-4xl">
                      Simplifying the way you travel.
                    </h3>

                    <p className="mt-5 max-w-lg text-sm leading-8 text-white/75 sm:text-base">
                      To simplify the hotel discovery experience by
                      connecting travelers with great stays, competitive
                      rates and useful information they can trust.
                    </p>

                  </div>

                  <div className="mt-10 flex items-center gap-3 border-t border-white/15 pt-5">

                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FCEBEC]">
                      <CheckCircleIcon className="h-5 w-5 text-[#8B0000]" />
                    </div>

                    <span className="text-xs font-semibold tracking-wide text-white/70">
                      Making hotel discovery simpler
                    </span>

                  </div>

                </div>
              </div>

              {/* VISION */}
              <div className="group relative min-h-[370px] overflow-hidden rounded-[28px] border border-[#DCE6EC] bg-[#F4F8FA] p-7 transition duration-500 hover:-translate-y-1 hover:border-[#0B5552]/25 hover:shadow-[0_25px_70px_rgba(15,35,50,0.08)] sm:p-10 lg:p-12">

                <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-[#0B5552]/5 blur-3xl transition duration-700 group-hover:bg-[#0B5552]/10" />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-[#8B0000] shadow-sm">
                      <GlobeAltIcon className="h-8 w-8" />
                    </div>

                    <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.22em] text-[#8B0000]">
                      Our Vision
                    </p>

                    <h3 className="mt-3 max-w-lg text-2xl font-extrabold leading-tight text-[#172B35] sm:text-3xl lg:text-4xl">
                      A better way to discover Dubai.
                    </h3>

                    <p className="mt-5 max-w-lg text-sm leading-8 text-[#71808A] sm:text-base">
                      To become a trusted platform for hotel discovery
                      and travel inspiration, helping travelers find
                      memorable stays and make confident choices
                      throughout their Dubai journey.
                    </p>

                  </div>

                  <div className="mt-10 flex items-center gap-3 border-t border-[#DCE6EC] pt-5">

                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0B5552]/10">
                      <CheckCircleIcon className="h-4 w-4 text-[#8B0000]" />
                    </span>

                    <span className="text-xs font-semibold tracking-wide text-[#71808A]">
                      Discover more with confidence
                    </span>

                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className="relative overflow-hidden bg-[#F5F8FA] py-16 sm:py-20 lg:py-28">

          <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-[#0B5552]/5 blur-3xl" />

          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* HEADING */}
            <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">

              <div className="max-w-2xl">

                <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-[#0B5552]/10 bg-white px-4 py-2 shadow-sm">

                  <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />

                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B0000]">
                    Why Dubai Hotel Rates
                  </span>

                </div>

                <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-[#172B35] sm:text-4xl lg:text-5xl">

                  Travel With

                  <span className="text-[#8B0000]">
                    {" "}Confidence
                  </span>

                </h2>

                <div className="mt-5 h-1 w-16 rounded-full bg-[#D7A74A]" />

              </div>

              <p className="max-w-md text-sm leading-8 text-[#71808A] lg:text-right sm:text-base">
                Everything we do is focused on making your hotel search
                easier, more transparent and more enjoyable.
              </p>

            </div>

            {/* FEATURE CARDS */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">

              {features.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="group relative overflow-hidden rounded-[24px] border border-[#DCE6EC] bg-white p-6 transition-all duration-500 hover:-translate-y-2 hover:border-[#0B5552]/25 hover:shadow-[0_25px_60px_rgba(15,35,50,0.09)] sm:p-7"
                  >

                    {/* NUMBER */}
                    <span className="absolute right-6 top-5 text-4xl font-extrabold tracking-tight text-[#F0F4F6] transition-colors duration-300 group-hover:text-[#E8F3F2]">
                      {item.number}
                    </span>

                    {/* ICON */}
                    <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FCEBEC] text-[#8B0000] transition-all duration-300 group-hover:bg-[#8B0000] group-hover:text-white group-hover:shadow-lg">
                      <Icon className="h-7 w-7" />
                    </div>

                    {/* TITLE */}
                    <h3 className="mt-7 text-lg font-bold text-[#172B35] transition-colors duration-300 group-hover:text-[#8B0000]">
                      {item.title}
                    </h3>

                    {/* DESCRIPTION */}
                    <p className="mt-3 min-h-[96px] text-sm leading-7 text-[#71808A]">
                      {item.desc}
                    </p>

                    {/* BOTTOM */}
                    <div className="mt-6 flex items-center justify-between border-t border-[#EDF1F3] pt-5">

                      <div className="flex items-center gap-2 text-xs font-semibold text-[#8B0000]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />
                        Travel with confidence
                      </div>

                      <ArrowUpRightIcon className="h-4 w-4 text-[#A5B2B9] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#8B0000]" />

                    </div>

                    {/* BOTTOM ACCENT */}
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-[#0B5552] to-[#D7A74A] transition-all duration-500 group-hover:w-full" />

                  </div>
                );
              })}

            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <CTASection />

      </div>
    </>
  );
}